import { useState, useEffect } from 'react';
import { useApiUrl, useCustom } from '@refinedev/core';
import { useNavigate, useParams } from 'react-router-dom';
import { getIdToken } from '@/providers/auth-provider';
import { toast } from '@/hooks/useToast';
import { ChevronLeft, Save, Loader2, AlertCircle } from 'lucide-react';

interface Promotion {
  id: string;
  name: string;
  description: string | null;
  status: 'draft' | 'active' | 'paused' | 'ended' | 'archived';
  ends_at: string | null;
  max_total_claims: number | null;
  max_claims_per_user: number;
}

export function PromotionEditPage() {
  const apiUrl = useApiUrl();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [form, setForm] = useState({
    name: '',
    description: '',
    ends_at: '',
    max_total_claims: '' as number | '',
    max_claims_per_user: 1,
    reason: '',
  });

  const { data, isLoading } = useCustom<Promotion>({
    url: `/v1/admin/promotions/${id}`,
    method: 'get',
  });
  const promo = data?.data;

  useEffect(() => {
    if (promo) {
      setForm({
        name: promo.name ?? '',
        description: promo.description ?? '',
        ends_at: promo.ends_at ? new Date(promo.ends_at).toISOString().slice(0, 16) : '',
        max_total_claims: promo.max_total_claims ?? '',
        max_claims_per_user: promo.max_claims_per_user ?? 1,
        reason: '',
      });
    }
  }, [promo]);

  if (isLoading || !promo) {
    return <div className="p-6">Loading…</div>;
  }

  const isDraft = promo.status === 'draft';
  const isActive = promo.status === 'active';
  const isReadOnly = !isDraft && !isActive;

  const handleSubmit = async () => {
    if (!form.name.trim() || form.reason.trim().length < 3) return;
    setSubmitting(true);
    setError(null);
    try {
      const idToken = await getIdToken();
      const body: Record<string, unknown> = {
        name: form.name.trim(),
        description: form.description.trim() || null,
        ends_at: form.ends_at ? new Date(form.ends_at).toISOString() : null,
        reason: form.reason.trim(),
      };
      if (isDraft) {
        body.max_total_claims = form.max_total_claims === '' ? null : Number(form.max_total_claims);
        body.max_claims_per_user = Number(form.max_claims_per_user);
      }

      const r = await fetch(`${apiUrl}/v1/admin/promotions/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
        body: JSON.stringify(body),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({ message: 'Update failed' }));
        throw new Error((err as { message?: string }).message ?? `HTTP ${r.status}`);
      }
      toast({ title: 'Promotion updated' });
      navigate(`/promotions/${id}`);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="p-6 max-w-3xl mx-auto">
      <div className="mb-6">
        <button
          onClick={() => navigate(`/promotions/${id}`)}
          className="text-sm text-muted-foreground hover:text-foreground mb-2 inline-flex items-center gap-1"
        >
          <ChevronLeft className="w-3 h-3" /> Back to {promo.name}
        </button>
        <h1 className="text-2xl font-semibold">Edit Promotion</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Status: <span className="font-medium">{promo.status}</span>
          {isDraft && ' — all fields editable'}
          {isActive && ' — limited fields editable (name, description, ends_at)'}
          {isReadOnly && ' — read-only. Use lifecycle transitions on the detail page to revert/edit.'}
        </p>
      </div>

      {isReadOnly ? (
        <div className="p-4 bg-muted/50 border rounded-md text-sm">
          Cannot edit a promotion in <span className="font-medium">{promo.status}</span> status. Return to the detail
          page to use lifecycle transitions.
        </div>
      ) : (
        <div className="space-y-5">
          <div>
            <label className="block text-sm font-medium mb-1">
              Name <span className="text-destructive">*</span>
            </label>
            <input
              type="text"
              maxLength={100}
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="w-full px-3 py-2 border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-primary/30"
            />
            <p className="text-xs text-muted-foreground mt-1">{form.name.length}/100. Internal admin name.</p>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Description</label>
            <textarea
              rows={3}
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="Internal admin notes — never shown to users"
              className="w-full px-3 py-2 border rounded-md bg-background resize-none focus:outline-none focus:ring-2 focus:ring-primary/30"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Ends at</label>
            <input
              type="datetime-local"
              value={form.ends_at}
              onChange={(e) => setForm({ ...form, ends_at: e.target.value })}
              className="w-full px-3 py-2 border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-primary/30"
            />
            <p className="text-xs text-muted-foreground mt-1">Leave blank for no expiry. Coupons inherit this on claim.</p>
          </div>

          {isDraft && (
            <>
              <div>
                <label className="block text-sm font-medium mb-1">Max total claims</label>
                <input
                  type="number"
                  min={1}
                  value={form.max_total_claims}
                  onChange={(e) =>
                    setForm({ ...form, max_total_claims: e.target.value === '' ? '' : Number(e.target.value) })
                  }
                  placeholder="Unlimited"
                  className="w-full px-3 py-2 border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-primary/30"
                />
                <p className="text-xs text-muted-foreground mt-1">Hard cap across all users. Leave blank for unlimited.</p>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Max claims per user</label>
                <input
                  type="number"
                  min={1}
                  value={form.max_claims_per_user}
                  onChange={(e) =>
                    setForm({ ...form, max_claims_per_user: Math.max(1, Number(e.target.value) || 1) })
                  }
                  className="w-full px-3 py-2 border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-primary/30"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  How many times the same user can claim this promotion.
                </p>
              </div>
            </>
          )}

          <div>
            <label className="block text-sm font-medium mb-1">
              Reason <span className="text-destructive">*</span>
            </label>
            <input
              type="text"
              value={form.reason}
              onChange={(e) => setForm({ ...form, reason: e.target.value })}
              placeholder="Why are you editing? (audit trail)"
              className="w-full px-3 py-2 border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-primary/30"
            />
            <p className="text-xs text-muted-foreground mt-1">Required, ≥ 3 chars. Persisted to admin_audit_log.</p>
          </div>

          {error && (
            <div className="flex items-start gap-2 p-3 bg-destructive/10 border border-destructive/30 rounded-md text-sm">
              <AlertCircle className="w-4 h-4 text-destructive flex-shrink-0 mt-0.5" />
              <div className="text-destructive">{error}</div>
            </div>
          )}

          <div className="flex items-center justify-end gap-2 pt-4 border-t">
            <button
              type="button"
              onClick={() => navigate(`/promotions/${id}`)}
              disabled={submitting}
              className="px-4 py-2 border rounded-md text-sm hover:bg-muted/50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={!form.name.trim() || form.reason.trim().length < 3 || submitting}
              onClick={handleSubmit}
              className="inline-flex items-center gap-1 px-4 py-2 bg-primary text-primary-foreground rounded-md text-sm hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              {submitting ? 'Saving…' : 'Save changes'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
